import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import {
  API_V2,
  API_V3,
  ApiV2Response,
  ApiV3Response,
  Environment,
  Paginated,
  PRIVATE_API,
  Run,
} from '@dbt-labs/app-nav';

import { CostInsightsConnectionTestStatusOverview } from '../../typings/costInsights';
import { ListRunFilters } from './types';
import { createV2Page } from './utils';

export interface ProjectDeprecationResponse {
  last_detected_at?: string;
  fusion_eligibility?: FusionEligibility;
}

export enum FusionEligibility {
  Ready = 'FUSION_READY',
  Limited = 'FUSION_LIMITED',
  NotReady = 'FUSION_NOT_READY',
  Unknown = 'FUSION_UNKNOWN',
  ReadyNonconformant = 'FUSION_READY_NONCONFORMANT',
}

/**
 * Fetches the project deprecation response from the private API.
 * @param accountId - The account ID
 * @param projectId - The project ID
 * @returns The project deprecation response.
 */
export const getProjectDeprecation = async (
  accountId: number,
  projectId: number,
): Promise<ProjectDeprecationResponse> => {
  const response = await axios.get<ApiV3Response<ProjectDeprecationResponse>>(
    `${PRIVATE_API}/accounts/${accountId}/projects/${projectId}/deprecation/`,
  );

  return response.data.data;
};

/**
 * Fetches the deprecation warning for a project from the private API.
 * @param accountId - The account ID
 * @param projectId - The project ID
 * @param pause - Whether to pause the query
 * @returns The deprecation warning.
 */
export const useDeprecationWarningForProject = (
  accountId?: number,
  projectId?: number,
  pause?: boolean,
) => {
  return useQuery<ProjectDeprecationResponse, AxiosError>({
    queryKey: ['project-deprecation-warning', accountId, projectId],
    queryFn: () => getProjectDeprecation(accountId ?? 0, projectId ?? 0),
    enabled: !pause && !!accountId && !!projectId,
    // don't retry on 404s
    retry: false,
    retryOnMount: false,
  });
};

const listRuns = async (
  accountId: number,
  filters?: ListRunFilters,
  pageOffset: number = 0,
  pageSize: number = 5,
  includedRelated?: string[],
  orderBy?: string,
): Promise<Paginated<Run[]>> => {
  const response = await axios.get<ApiV2Response<Run>>(
    `${API_V2}/accounts/${accountId}/runs/`,
    {
      params: {
        limit: pageSize,
        offset: pageOffset,
        order_by: orderBy ?? '-id',
        include_related: JSON.stringify(
          includedRelated ?? [
            'job',
            'trigger',
            'environment',
            'repository',
            'used_repo_cache',
            'deprecation',
          ],
        ),
        ...filters,
      },
    },
  );

  return createV2Page(response, pageSize);
};

/**
 * Fetches the runs for a project from the API v2.
 * @param accountId - The account ID
 * @param projectId - The project ID
 * @param filters - The filters to apply to the runs
 * @param limit - The limit of runs to return
 * @param orderBy - The order by field
 * @param includeRelated - The related objects to include
 * @param disabled - Whether to disable the query
 * @returns The runs for the project.
 */
export const useRunsForProject = (
  accountId?: number,
  projectId?: number,
  filters?: ListRunFilters,
  limit?: number,
  orderBy?: string,
  includeRelated?: string[],
  disabled: boolean = false,
) => {
  return useInfiniteQuery<Paginated<Run[]>, AxiosError>({
    queryKey: [`project-runs`, accountId, projectId, filters],
    queryFn: ({ pageParam: continuationToken }) =>
      listRuns(
        accountId ?? 0,
        { ...filters, project_id: projectId ?? 0 },
        continuationToken as number,
        limit,
        includeRelated,
        orderBy,
      ),
    enabled: !!accountId && !!projectId && !disabled,
    getNextPageParam: (lastPage) => lastPage.nextPageOffset,
    initialPageParam: 0,
  });
};

const getEnvironment = async (
  accountId: number,
  environmentId: number,
  includeRelated: string[] = [],
): Promise<Environment> => {
  const response = await axios.get<ApiV3Response<Environment>>(
    `${API_V3}/accounts/${accountId}/environments/${environmentId}/`,
    {
      params: {
        include_related: includeRelated.length ? `[${includeRelated.join(',')}]` : '[]',
      },
    },
  );

  return response?.data?.data;
};

/**
 * Fetches the environment from the API v3.
 * @param environmentId - The environment ID
 * @param accountId - The account ID
 * @param includeRelated - The related objects to include
 * @param preventUpdate - Whether to prevent refetching. When true, the query will still run initially
 *                        (or use cached data if available) but will not refetch on window focus, mount, or reconnect.
 *                        The query will also have infinite staleTime to prevent automatic refetches.
 * @param pause - Whether to pause the query
 * @returns The environment.
 */
export const useEnvironment = ({
  environmentId,
  accountId,
  includeRelated,
  preventUpdate,
  pause,
}: {
  environmentId: number | string | undefined;
  accountId: number | undefined;
  includeRelated?: string[];
  preventUpdate?: boolean;
  pause?: boolean;
}) => {
  const numericEnvironmentId = Number(environmentId);
  const safeEnvironmentId = Number.isNaN(numericEnvironmentId)
    ? 0
    : numericEnvironmentId;

  return useQuery<Environment, AxiosError>({
    queryKey: ['environment', accountId, environmentId, includeRelated],
    queryFn: () => getEnvironment(accountId ?? 0, safeEnvironmentId, includeRelated),
    enabled: !!(accountId && environmentId && environmentId !== 'new') || !pause,
    refetchOnWindowFocus: !preventUpdate,
    staleTime: preventUpdate ? Infinity : undefined,
    refetchOnMount: !preventUpdate,
    refetchOnReconnect: !preventUpdate,
  });
};

export interface FusionStatus {
  is_eligible: boolean;
}

const getFusionStatus = async (
  accountId: number,
  projectId: number,
): Promise<FusionStatus> => {
  const response = await axios.get<FusionStatus>(
    `${PRIVATE_API}/accounts/${accountId}/projects/${projectId}/fusion/status/`,
  );
  return response.data;
};

/**
 * Fetches the fusion status for a project from the private API.
 * @param accountId - The account ID
 * @param projectId - The project ID
 * @param pause - Whether to pause the query
 * @returns The fusion status.
 * is_eligible: boolean - Whether the project is eligible for fusion
 */
export const useFusionStatus = (
  accountId?: number,
  projectId?: number,
  pause?: boolean,
) => {
  return useQuery<FusionStatus, AxiosError>({
    queryKey: ['fusion-status', accountId, projectId],
    queryFn: () => getFusionStatus(accountId ?? 0, projectId ?? 0),
    enabled: !pause && !!accountId && !!projectId,
  });
};

export const getCostInsightsConnectionTestStatus = async (
  accountId: number,
  connectionId: number,
): Promise<CostInsightsConnectionTestStatusOverview> => {
  const response = await axios.get<
    ApiV3Response<CostInsightsConnectionTestStatusOverview>
  >(
    `${API_V3}/accounts/${accountId}/connections/${connectionId}/cost-insights/test/status/`,
  );
  return response.data.data;
};

/**
 * Fetches the Cost Insights connection test status from the API v3.
 * Polls every 3 seconds while the connection is actively being tested.
 * @param accountId - The account ID
 * @param connectionId - The connection ID
 * @returns The connection test status overview.
 */
export const useCostInsightsConnectionTestStatus = (
  accountId?: number,
  connectionId?: number,
) => {
  return useQuery<CostInsightsConnectionTestStatusOverview, AxiosError>({
    queryKey: ['costInsightsConnectionTestStatus', accountId, connectionId],
    queryFn: () =>
      getCostInsightsConnectionTestStatus(accountId ?? 0, connectionId ?? 0),
    enabled: !!accountId && !!connectionId,
    refetchInterval: (query) => {
      if (query.state.data?.status === 'testing') {
        return 3000;
      }
      return false;
    },
  });
};

export interface JobsSAOEnablement {
  totalJobs: number;
  jobsWithSAOEnabled: number;
}

interface JobsSAOEnablementResponse {
  enabled_job_count: number;
  total_job_count: number;
}

const getJobsSAOEnablement = async (
  accountId: number,
  projectId: number,
): Promise<JobsSAOEnablement> => {
  const response = await axios.get<JobsSAOEnablementResponse>(
    `${PRIVATE_API}/accounts/${accountId}/projects/${projectId}/sao-enablement-status/`,
  );
  const { enabled_job_count, total_job_count } = response.data;
  return {
    totalJobs: total_job_count,
    jobsWithSAOEnabled: enabled_job_count,
  };
};

/**
 * Fetches the SAO enablement status for jobs in a project.
 * @param accountId - The account ID
 * @param projectId - The project ID
 * @param pause - Whether to pause the query
 * @returns The SAO enablement status.
 * - totalJobs: Total number of jobs in the project
 * - jobsWithSAOEnabled: Number of jobs with SAO enabled
 */
export const useJobsWithSAOEnablement = (
  accountId?: number,
  projectId?: number,
  pause?: boolean,
) => {
  return useQuery<JobsSAOEnablement, AxiosError>({
    queryKey: ['jobs-sao-enablement', accountId, projectId],
    queryFn: () => getJobsSAOEnablement(accountId ?? 0, projectId ?? 0),
    enabled: !pause && !!accountId && !!projectId,
  });
};
